/**
 * A licence's Customer Success Manager (#583), shared by the two forms that set
 * it: New licence (`add-licence`) and Edit licence (`licence-detail`).
 *
 * Who may be picked mirrors the backend rule (`csm-policy.ts`): any Invyo staff
 * account, whatever its role (#630), whose access is live: active, not
 * archived, invitation accepted. The server re-checks every pick; filtering
 * here only keeps the select from offering a name it would refuse.
 *
 * A CSM already on record stays on the licence even once they no longer qualify,
 * so the Edit form offers them too (as the current value) rather than blanking
 * the field.
 *
 * Pure (no React, no MUI, no fetch).
 */

import { SUBSCRIPTION_STATUS } from '@shared/constants/subscriptionStatus';
import type { LicenceCsm, UserResponse } from '@shared/schemas';

export type { LicenceCsm };

/** Lifecycle of the candidates' load; the select waits for it. */
export type CsmCandidatesStatus = 'idle' | 'loading' | 'loaded' | 'failed';

export interface CsmCandidatesState {
  items: LicenceCsm[];
  status: CsmCandidatesStatus;
}

export const emptyCsmCandidates: CsmCandidatesState = { items: [], status: 'idle' };

/** Fields of a user the eligibility rule reads. */
type CsmUserRow = Pick<
  UserResponse,
  'is_admin' | 'subscription_status' | 'archived_at'
>;

export function isEligibleCsm(user: CsmUserRow): boolean {
  return (
    user.is_admin &&
    user.subscription_status === SUBSCRIPTION_STATUS.ACTIVE &&
    !user.archived_at
  );
}

/** "Chloé Martin", or the email when the account has no name. */
export function csmLabel(csm: LicenceCsm): string {
  return `${csm.first_name} ${csm.last_name}`.trim() || (csm.email ?? '');
}

export const CSM_REQUIRED_MESSAGE = 'A Customer Success Manager is required.';

/**
 * Required since #695, in New licence and Edit licence alike: every licence
 * names a CSM, whom the client's "Contact My Customer Success Manager" form
 * writes to. Mirrors the backend (`decideLicenceEdit`, `CreateLicenceRequestSchema`).
 */
export function validateCsm(csmUserId: string): string | undefined {
  return csmUserId ? undefined : CSM_REQUIRED_MESSAGE;
}

/** The users who may be picked as a CSM, by name; from the admin index. */
export function toCsmCandidates(users: readonly UserResponse[]): LicenceCsm[] {
  return users
    .filter(isEligibleCsm)
    .map(({ id, first_name, last_name, email, avatar_url }) => ({
      id,
      first_name,
      last_name,
      email,
      avatar_url,
    }))
    .sort((a, b) => csmLabel(a).localeCompare(csmLabel(b), 'en'));
}

/**
 * What the select offers: the eligible candidates, plus the CSM on record when
 * they are not among them (archived since, or their access lapsed), so the
 * form shows the current value instead of an empty field.
 */
export function csmOptions(
  candidates: readonly LicenceCsm[],
  current: LicenceCsm | null,
): LicenceCsm[] {
  if (!current || candidates.some((c) => c.id === current.id)) return [...candidates];
  return [current, ...candidates];
}
